import { api, setAccessToken, refreshSession, type SessionResponse } from './client';

export type AuthUser = SessionResponse['user'];

async function startSession(promise: Promise<{ data: SessionResponse }>): Promise<AuthUser> {
  const res = await promise;
  setAccessToken(res.data.access_token);
  return res.data.user;
}

export function login(email: string, password: string): Promise<AuthUser> {
  return startSession(api.post('/api/auth/login', { email, password }));
}

/** No session is issued at registration — the emailed code completes it. */
export async function register(
  email: string,
  password: string,
  display_name: string,
): Promise<{ email: string }> {
  const res = await api.post<{ verification_required: true; email: string }>('/api/auth/register', {
    email,
    password,
    display_name,
  });
  return { email: res.data.email };
}

export function googleCallback(
  code: string,
  code_verifier: string | undefined,
  redirect_uri: string,
): Promise<AuthUser> {
  return startSession(api.post('/api/auth/google/callback', { code, code_verifier, redirect_uri }));
}

export async function logout(): Promise<void> {
  try {
    await api.post('/api/auth/logout');
  } finally {
    setAccessToken(null);
  }
}

export async function tryRefresh(): Promise<AuthUser | null> {
  const session = await refreshSession();
  return session?.user ?? null;
}

/** Entering a valid code verifies the account AND starts the session. */
export function verifyEmail(email: string, code: string): Promise<AuthUser> {
  return startSession(api.post('/api/auth/verify-email', { email, code }));
}

export async function resendVerification(email: string): Promise<void> {
  await api.post('/api/auth/verify-email/resend', { email });
}

export async function requestPasswordReset(email: string): Promise<void> {
  await api.post('/api/auth/password/reset-request', { email });
}

export async function resetPassword(
  email: string,
  code: string,
  new_password: string,
): Promise<void> {
  await api.post('/api/auth/password/reset', { email, code, new_password });
}

export async function setPassword(password: string): Promise<void> {
  await api.post('/api/auth/password/set', { password });
}
